import React, { memo, useEffect, useState, useMemo } from 'react';
import { Modal, Spinner } from 'react-bootstrap';
import { Button, ImageIcon } from '@saleshandy/design-system';
import { ChevronRight, Cross } from '@saleshandy/icons';
import { useDispatch } from 'react-redux';
import { accessibleOnClick } from '../../../../../../../../shared/utils/accessible-on-click';
import { COUNTRY_ISO_FLAG_MAP } from '../../constants';
import { PhoneNumberCartItem } from '../../../../../../types/phone-numbers';
import { RequestStatus } from '../../../../../../../../shared/enums/request-status';
import {
  getIsRequestPending,
} from '../../../../../../../../shared/utils/get-request-status';
import toaster, { Theme } from '../../../../../../../../shared/toaster';
import { resetRemovePhoneNumberFromCartRequest } from '../../../../../../dialer-slice';
import { executeOnRequestStatus } from '../../../../../../../../shared/utils';
import { PhoneNumberPurchaseStep } from '../../purchase-phone-number-tabs';

interface PurchasePhoneNumberCartProps {
  show: boolean;
  onClose: () => void;
  phoneNumbers: PhoneNumberCartItem[];
  isNextButtonDisabled: boolean;
  isNextButtonLoading: boolean;
  onNextButtonClick: () => void;
  nextButtonText: string;
  onClearAllClick: () => void;
  removePhoneNumberFromCart: (phoneNumber: string) => void;
  totalPrice: string;
  removePhoneNumberFromCartRequestState: {
    [phoneNumber: string]: {
      status: RequestStatus;
      message: string;
      error: any;
    };
  };
  setActiveKey: (key: PhoneNumberPurchaseStep) => void;
}

const PurchasePhoneNumberCart: React.FC<PurchasePhoneNumberCartProps> = ({
  show,
  onClose,
  phoneNumbers,
  isNextButtonDisabled,
  isNextButtonLoading,
  onNextButtonClick,
  nextButtonText,
  onClearAllClick,
  removePhoneNumberFromCart,
  totalPrice,
  removePhoneNumberFromCartRequestState,
  setActiveKey,
}) => {
  const [animationClass, setAnimationClass] = useState('hide');
  const dispatch = useDispatch();
  useEffect(() => {
    if (show) {
      setAnimationClass('show');
    } else {
      setAnimationClass('hide');
    }
  }, [show]);

  const phoneNumbersCount = useMemo(() => phoneNumbers.length, [phoneNumbers]);

  const clearAllKey = '__clearAll__';
  const isClearAllRequestPending = getIsRequestPending(
    removePhoneNumberFromCartRequestState[clearAllKey]?.status,
  );

  // Handle clear all success: close drawer and show success toaster
  useEffect(() => {
    executeOnRequestStatus({
      status: removePhoneNumberFromCartRequestState[clearAllKey]?.status,
      onSuccess: () => {
        if (show) {
          onClose();
          toaster.success('Phone numbers removed successfully from your cart', {
            theme: Theme.New,
          });
          dispatch(resetRemovePhoneNumberFromCartRequest(clearAllKey));
          setActiveKey(PhoneNumberPurchaseStep.SelectNumber);
        }
      },
    });
  }, [removePhoneNumberFromCartRequestState[clearAllKey]?.status]);

  useEffect(() => {
    if(phoneNumbersCount === 0) {
      onClose();
      setActiveKey(PhoneNumberPurchaseStep.SelectNumber);
    }
  }, [phoneNumbersCount]);
  return (
    <Modal
      show={show}
      className={`purchase-phone-number-cart ${animationClass}`}
      backdrop="static"
    >
      <Modal.Header>
        <Modal.Title>Cart</Modal.Title>
        <div className="close-icon-btn" {...accessibleOnClick(onClose)}>
          <Cross width={20} height={20} />
        </div>
      </Modal.Header>
      <Modal.Body>
        <div className="cart-content">
          <div className="cart-section-header">
            <span className="cart-section-title">Phone Numbers</span>
            <div className="phone-numbers-badge">{phoneNumbersCount}</div>
          </div>
          <div className="cart-data-list">
            {phoneNumbers.map((phoneNumber) => (
              <div className="cart-data-item" key={phoneNumber.phoneNumber}>
                <div className="cart-data-item-content">
                  <div className="cart-data-item-header">
                    <div className="flag-icon">
                      <ImageIcon
                        src={COUNTRY_ISO_FLAG_MAP[phoneNumber.countryISOCode]}
                        alt={`${phoneNumber.countryISOCode} flag`}
                        width={20}
                        height={16}
                      />
                    </div>
                    <span className="cart-data-item-name">
                      {phoneNumber.phoneNumber}
                    </span>
                    <span className="cart-data-item-location">
                      {phoneNumber.formattedRegion}
                    </span>
                    {phoneNumber.isFreeNumber && (
                      <div className="badge-chip free">
                        <span>Free</span>
                      </div>
                    )}
                  </div>
                </div>
                <div className="cart-data-item-info">
                  <span className="cart-data-item-price">
                    {phoneNumber.pricePerQuarter}
                  </span>
                  {(() => {
                    const normalizedPhoneNumber = phoneNumber.phoneNumber.replace(
                      /\s/g,
                      '',
                    );
                    const isRemoveRequestPending = getIsRequestPending(
                      removePhoneNumberFromCartRequestState[
                        normalizedPhoneNumber
                      ]?.status,
                    );

                    if (isRemoveRequestPending) {
                      return (
                        <div className="cart-data-item-remove-spinner">
                          <Spinner
                            className="spinner--blue bw-1"
                            animation="border"
                            size="sm"
                          />
                        </div>
                      );
                    }

                    return (
                      <div
                        className="cart-data-item-remove"
                        {...accessibleOnClick(() =>
                          removePhoneNumberFromCart(phoneNumber.phoneNumber),
                        )}
                      >
                        <Cross width={14} height={14} />
                      </div>
                    );
                  })()}
                </div>
              </div>
            ))}
          </div>
        </div>
      </Modal.Body>
      <Modal.Footer>
        <div className="cart-info">
          <div className="cart-info-left">
            <p>
              {phoneNumbersCount}{' '}
              {phoneNumbersCount === 1 ? 'Phone Number' : 'Phone Number(s)'}
            </p>
          </div>

          <div className="cart-info-right">
            <p>{totalPrice}</p>
          </div>
        </div>

        <div className="cart-actions">
          <Button
            variant="link"
            className="clear-all-btn"
            onClick={onClearAllClick}
            disabled={phoneNumbersCount === 0 || isClearAllRequestPending}
          >
            {isClearAllRequestPending ? (
              <Spinner
                className="spinner--blue bw-1"
                animation="border"
                size="sm"
              />
            ) : (
              'Clear all'
            )}
          </Button>
          <Button
            disabled={isNextButtonDisabled}
            isLoading={isNextButtonLoading}
            onClick={onNextButtonClick}
            loadingText={nextButtonText}
            icon={ChevronRight}
            iconPlacement="right"
            className="cart-next-button"
          >
            {nextButtonText}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  );
};

export default memo(PurchasePhoneNumberCart);
